import { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { uploadMediaInput } from "@/lib/uploads";
import { cn } from "@/lib/utils";

const TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_BYTES = 10 * 1024 * 1024;

export function SourceImages({ sources, onChange, max, types = TYPES, label = "image", testId = "source-images" }) {
  const inputRef = useRef(null);
  const [pending, setPending] = useState(0);
  const [over, setOver] = useState(false);

  const add = async (files) => {
    const room = max - sources.length - pending;
    const picked = [...files].slice(0, Math.max(room, 0));
    if (files.length > picked.length) toast.error(`You can edit with up to ${max} images.`);
    for (const file of picked) {
      if (!types.includes(file.type)) { toast.error(types.includes("video/mp4") ? "Use an MP4 video." : "Use a PNG, JPEG or WebP image."); continue; }
      if (file.size > (file.type === "video/mp4" ? 5 * MAX_BYTES : MAX_BYTES)) { toast.error("Images can be up to 10 MB and videos up to 50 MB."); continue; }
      setPending((n) => n + 1);
      try {
        const r = await uploadMediaInput(file);
        onChange((list) => [...list, { id: r.id, preview_url: r.preview_url, video: file.type === "video/mp4" }]);
      } catch (e) {
        toast.error(e.message);
      } finally {
        setPending((n) => n - 1);
      }
    }
  };

  const onDrop = (e) => { e.preventDefault(); setOver(false); add(e.dataTransfer.files); };
  const full = sources.length + pending >= max;

  return (
    <div className="flex flex-wrap gap-2" data-testid={testId} onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={onDrop}>
      {sources.map((s, i) => (
        <div key={s.id} className="group relative h-20 w-20 overflow-hidden rounded-xl border border-white/10 bg-ink-900" data-testid={`source-image-${i}`}>
          {s.video ? <video src={s.preview_url} muted playsInline className="h-full w-full object-cover" /> : <img src={s.preview_url} alt={`Reference ${i + 1}`} className="h-full w-full object-cover" />}
          <button type="button" onClick={() => onChange((list) => list.filter((x) => x.id !== s.id))} aria-label="Remove image" data-testid={`source-image-remove-${i}`}
            className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/70 text-zinc-200 transition-colors hover:bg-black hover:text-white">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
      {Array.from({ length: pending }).map((_, i) => (
        <div key={`p${i}`} className="flex h-20 w-20 items-center justify-center rounded-xl border border-white/10 bg-ink-900" data-testid="source-image-uploading">
          <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
        </div>
      ))}
      {!full && (
        <button type="button" onClick={() => inputRef.current?.click()} data-testid="source-image-add"
          className={cn("flex h-20 min-w-[5rem] flex-col items-center justify-center gap-1 rounded-xl border border-dashed px-3 text-[11px] transition-colors duration-200",
            over ? "border-white/50 bg-white/[0.06] text-white" : "border-white/15 text-zinc-500 hover:border-white/30 hover:text-zinc-200")}>
          <ImagePlus className="h-4 w-4" />
          {sources.length ? "Add more" : `Add ${label}`}
        </button>
      )}
      <input ref={inputRef} type="file" accept={types.join(",")} multiple hidden data-testid="source-image-input"
        onChange={(e) => { add(e.target.files); e.target.value = ""; }} />
    </div>
  );
}
